import { motion } from 'framer-motion';
import { Section } from '@/components/Section';

const paragraphs = [
  "I've spent my life working at the edge of technology\u2014not as a spectator, but as a builder.",
  "My path began in the late 1970s, when computer science wasn't a trend\u2014it was a frontier. From early operations and personal computer integration to networking and desktop application development, I've worked across the full evolution of modern computing. That work has ranged from supporting small business systems to contributing to environments as complex as the USAF Satellite Control Network.",
  "Over time, I've learned that technology alone doesn't create value\u2014integration does. Systems that actually work. Processes that people can use. Tools that produce real outcomes.",
  "That understanding has shaped everything I've built over the past several years.",
  "My recent work has focused on bringing artificial intelligence into practical use\u2014integrating it into business operations, publishing workflows, and creative systems. I've authored more than 40 titles across entrepreneurship, AI, and personal development, not as theory, but as applied thinking\u2014designed to help people make sense of what's changing and how to act on it.",
  "Through Use What You Got Publishing, I've developed systems that allow ideas to move from concept to completed work efficiently\u2014combining human insight with AI-assisted processes. With NewAudia, I'm building an interactive audiobook platform that transforms passive listening into engagement. With LifeCapsule AI, I'm working toward a system that captures and preserves the meaning behind a person's experiences\u2014turning memory into something structured, accessible, and lasting.",
];

const closingLines = [
  "I'm not here to impress people with what AI can do.",
  "I'm here to help people understand what they can do with it.",
];

const actionItems = [
  "make better decisions",
  "streamline their operations",
  "integrate emerging technologies like AI in a way that actually works",
  "and build something meaningful, efficient, and sustainable",
];

export function SingularVisionSection() {
  return (
    <Section dark id="singular-vision">
      <div className="max-w-4xl mx-auto">
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="text-sm text-primary font-medium uppercase tracking-widest mb-3 block">Singular Vision</span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-2" data-testid="singular-vision-heading">
            A Mission to Empower, Create, and Preserve
          </h2>
        </motion.div>

        <motion.blockquote
          className="text-2xl md:text-3xl text-white font-semibold leading-snug mb-10 border-l-4 border-primary pl-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          data-testid="singular-vision-lead"
        >
          To empower people to use what they've got to create something extraordinary.
        </motion.blockquote>

        <div className="space-y-5">
          {paragraphs.map((text, i) => (
            <motion.p
              key={text.slice(0, 40)}
              className="text-lg text-slate-300 leading-relaxed"
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
            >
              {text}
            </motion.p>
          ))}
        </div>

        <motion.div
          className="mt-10 mb-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-xl md:text-2xl text-white font-semibold leading-relaxed">
            But at this stage in my career, the mission is clear:
          </p>
          <p className="text-xl md:text-2xl text-primary font-semibold leading-relaxed mt-2">
            To take everything I've learned across decades of technology, business, and systems development—and help others use it.
          </p>
        </motion.div>

        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-lg text-slate-400 italic mb-4">Not in theory. Not in abstraction.</p>
          <p className="text-lg text-slate-300 leading-relaxed mb-4">
            In ways that allow entrepreneurs, operators, and builders to:
          </p>
          <ul className="space-y-2 pl-5 mb-6">
            {actionItems.map((item) => (
              <li key={item} className="text-lg text-slate-300 leading-relaxed list-disc marker:text-primary">{item}</li>
            ))}
          </ul>
        </motion.div>

        <div className="space-y-5">
          {closingLines.map((text, i) => (
            <motion.p
              key={text.slice(0, 40)}
              className="text-xl text-white font-semibold leading-relaxed"
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
            >
              {text}
            </motion.p>
          ))}
        </div>

        <motion.div
          className="border-t border-border/20 pt-8 mt-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-lg text-slate-300 leading-relaxed" data-testid="singular-vision-closing">
            As a speaker, advisor, and builder, my focus is simple: Cut through the noise, remove the confusion, and help people move forward with clarity and confidence in a rapidly changing technological landscape.
          </p>
        </motion.div>
      </div>
    </Section>
  );
}
